/**
 * LLM traces to LangWatch, over OTLP/HTTP, from a Cloudflare Worker.
 *
 * WHY NOT THE SDK
 * ---------------
 * Not because `nodejs_compat` is missing: all four Carrier workers set it, and
 * Node-shaped SDKs are already bundled here (stripe, @clerk/*). The objection
 * this codebase actually raises is bundle size. bedrock.ts records that
 * @aws-sdk/client-bedrock-runtime was rejected at roughly 1 MB and replaced
 * with aws4fetch plus a hand-written eventstream decoder. A tracing SDK would
 * be arguing against that precedent for less benefit than SigV4 gave.
 *
 * LangWatch accepts OTLP/HTTP at /api/otel/v1/traces with a project API key, so
 * a span is a JSON POST. That is about eighty lines, no dependency, and it
 * cannot break the Worker at build time.
 *
 * TRACING MUST NEVER COST AN ANSWER
 * ---------------------------------
 * Every function here swallows its own errors and returns void. A tracing
 * outage, a bad key, a changed schema: none of them may fail a call a customer
 * has already been charged for. Callers should also hand the promise to
 * `ctx.waitUntil()` so the POST does not sit in the response path.
 *
 * WHAT LEAVES THE WORKER
 * ----------------------
 * The same rule as the model call itself: no raw subscriber identifiers. The
 * org id and sub-account id are ours, not the subscriber's, so they are fine.
 * Prompt and completion text is opt-in per call via `captureContent`, because a
 * fleet answer quotes measured figures and an operator may not want those in a
 * third-party trace store by default.
 */

import { estimateCostUsd, type AiAttribution, type TokenUsage } from "./attribution.js";

export const DEFAULT_LANGWATCH_ENDPOINT = "https://app.langwatch.ai";

/** Wall-clock ceiling for the export. Short: it is telemetry, not the product. */
const EXPORT_TIMEOUT_MS = 3_000;

export interface TracingConfig {
  /** Project API key. Absent means tracing is off, which is a valid state. */
  apiKey?: string;
  /** Override for self-hosted LangWatch. */
  endpoint?: string;
  /** `production`, `staging`. Kept out of the span name so it can be filtered. */
  environment?: string;
  serviceName?: string;
}

export interface LlmSpan {
  /** Human name, e.g. "assistant_chat" or "high_cost_subscribers". */
  name: string;
  modelId: string;
  attribution: AiAttribution;
  usage: TokenUsage;
  startedAtMs: number;
  endedAtMs: number;
  streaming: boolean;
  /** Set when the call failed. Recorded as span status ERROR. */
  error?: string;
  /** Groups the turns of one conversation. */
  threadId?: string;
  /** Opt-in. See the header note on what leaves the Worker. */
  captureContent?: { input?: string; output?: string };
}

/** OTLP wants nanoseconds since the epoch, as a string. */
function nanos(ms: number): string {
  return `${Math.round(ms)}000000`;
}

/**
 * 16 random bytes as hex. OTLP requires a 32-char trace id and 16-char span id.
 * crypto.getRandomValues exists in Workers; no Node crypto import needed.
 */
function hexId(bytes: number): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return Array.from(buf)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

type AttrValue = { stringValue: string } | { intValue: string } | { boolValue: boolean };

function attr(key: string, value: string | number | boolean): { key: string; value: AttrValue } {
  if (typeof value === "boolean") return { key, value: { boolValue: value } };
  if (typeof value === "number") return { key, value: { intValue: String(Math.round(value)) } };
  return { key, value: { stringValue: value } };
}

/**
 * Build the OTLP payload for one LLM call.
 *
 * Attribute names follow what LangWatch documents: `langwatch.span.type` marks
 * it as an llm span, `langwatch.user.id` and `langwatch.thread.id` drive the
 * grouping in its UI, and the `gen_ai.*` keys are the OpenTelemetry standard so
 * the same span is legible to any other OTel backend.
 */
export function buildOtlpPayload(span: LlmSpan, config: TracingConfig): Record<string, unknown> {
  const cost = estimateCostUsd(span.usage);

  const attributes = [
    attr("langwatch.span.type", "llm"),
    attr("gen_ai.system", "aws.bedrock"),
    attr("gen_ai.request.model", span.modelId),
    attr("gen_ai.usage.input_tokens", span.usage.inputTokens),
    attr("gen_ai.usage.output_tokens", span.usage.outputTokens),
    attr("langwatch.gen_ai.streaming", span.streaming),
    // Cache counts are not in the OTel standard and are the difference between
    // a $1 and a $0.10 input token, so they are recorded explicitly.
    attr("carrier.usage.cache_read_tokens", span.usage.cacheReadTokens),
    attr("carrier.usage.cache_write_tokens", span.usage.cacheWriteTokens),
    // An estimate we own, not a billed figure. Named so nobody mistakes it.
    attr("carrier.cost.estimated_usd", String(cost.toFixed(6))),
  ];

  // The org is the billable tenant, so it is the "user" LangWatch groups by.
  if (span.attribution.orgId) attributes.push(attr("langwatch.user.id", span.attribution.orgId));
  if (span.threadId) attributes.push(attr("langwatch.thread.id", span.threadId));
  if (span.attribution.subAccountId) {
    attributes.push(attr("carrier.subaccount.id", span.attribution.subAccountId));
  }
  if (span.attribution.surface) attributes.push(attr("carrier.surface", span.attribution.surface));
  if (span.attribution.tier) attributes.push(attr("carrier.tier", span.attribution.tier));

  if (span.captureContent?.input) {
    attributes.push(attr("gen_ai.prompt", span.captureContent.input));
  }
  if (span.captureContent?.output) {
    attributes.push(attr("gen_ai.completion", span.captureContent.output));
  }

  return {
    resourceSpans: [
      {
        resource: {
          attributes: [
            attr("service.name", config.serviceName ?? "carrier"),
            attr("deployment.environment.name", config.environment ?? "production"),
          ],
        },
        scopeSpans: [
          {
            scope: { name: "carrier-ai", version: "1.0.0" },
            spans: [
              {
                traceId: hexId(16),
                spanId: hexId(8),
                name: span.name,
                kind: 3, // CLIENT
                startTimeUnixNano: nanos(span.startedAtMs),
                endTimeUnixNano: nanos(span.endedAtMs),
                attributes,
                status: span.error
                  ? { code: 2, message: span.error.slice(0, 400) }
                  : { code: 1 },
              },
            ],
          },
        ],
      },
    ],
  };
}

/**
 * Trim trailing slashes without a regex.
 *
 * This was `.replace(/\/+$/, "")`, which CodeQL flagged as polynomial ReDoS
 * (js/polynomial-redos, high). `\/+$` backtracks quadratically on a long run of
 * slashes that is not followed by end-of-string, so a value like
 * "https://x/" + "/".repeat(50_000) + "a" makes the match quadratic in its
 * length.
 *
 * The input is `LANGWATCH_ENDPOINT` from the Worker env rather than anything a
 * caller supplies, so it was not reachable in practice. Fixed anyway: the loop
 * is linear, obviously correct, and cheaper to read than an argument about why
 * the regex is safe here. A "not exploitable today" exception is a thing that
 * stops being true when the value's source changes.
 */
function withoutTrailingSlashes(url: string): string {
  let end = url.length;
  while (end > 0 && url.charCodeAt(end - 1) === 47 /* "/" */) end--;
  return url.slice(0, end);
}

/**
 * Export one span. Never throws, never rejects.
 *
 * Returns whether the export was accepted, which is useful in a test and
 * ignorable in production. Callers pass this to `ctx.waitUntil()`.
 */
export async function exportLlmSpan(
  span: LlmSpan,
  config: TracingConfig,
): Promise<boolean> {
  if (!config.apiKey) return false;

  const base = withoutTrailingSlashes(config.endpoint ?? DEFAULT_LANGWATCH_ENDPOINT);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), EXPORT_TIMEOUT_MS);

  try {
    const resp = await fetch(`${base}/api/otel/v1/traces`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${config.apiKey}`,
      },
      body: JSON.stringify(buildOtlpPayload(span, config)),
      signal: controller.signal,
    });
    return resp.ok;
  } catch {
    // Deliberately silent. A tracing failure is not a product failure, and a
    // console.error here would fill the Worker log during a LangWatch outage.
    return false;
  } finally {
    clearTimeout(timer);
  }
}
